/**
 * Component: ConversationSidebar
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R08, FRONTEND-WEB-V1-R13, FRONTEND-WEB-V1-R14
 *
 * Sidebar listing persistent conversation sessions grouped by time period.
 * Supports keyboard navigation (Arrow keys, Enter, Delete).
 *
 * GALEN-CHAT-V1-R08: Maintain conversation history until user resets or session expires.
 * FRONTEND-WEB-V1-R13: Touch targets min 44px.
 * FRONTEND-WEB-V1-R14: WCAG 2.1 AA compliant.
 */

import React, { useCallback, useRef, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { formatDistanceToNow, isToday, isYesterday, subDays, isAfter } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useConversations,
  useActiveConversationId,
  useConversationStore,
} from '@/stores/conversation-store';
import type { Conversation } from '@/stores/conversation-store';

export interface ConversationSidebarProps {
  onNewChat: () => void;
}

interface TimeGroup {
  label: string;
  conversations: Conversation[];
}

/**
 * Group conversations by time period: Today, Yesterday, Previous 7 Days, Older.
 */
function groupByTimePeriod(conversations: Conversation[]): TimeGroup[] {
  const now = new Date();
  const sevenDaysAgo = subDays(now, 7);

  const today: Conversation[] = [];
  const yesterday: Conversation[] = [];
  const week: Conversation[] = [];
  const older: Conversation[] = [];

  for (const conv of conversations) {
    const date = new Date(conv.updatedAt);
    if (isToday(date)) {
      today.push(conv);
    } else if (isYesterday(date)) {
      yesterday.push(conv);
    } else if (isAfter(date, sevenDaysAgo)) {
      week.push(conv);
    } else {
      older.push(conv);
    }
  }

  const result: TimeGroup[] = [];
  if (today.length > 0) result.push({ label: 'Today', conversations: today });
  if (yesterday.length > 0) result.push({ label: 'Yesterday', conversations: yesterday });
  if (week.length > 0) result.push({ label: 'Previous 7 Days', conversations: week });
  if (older.length > 0) result.push({ label: 'Older', conversations: older });

  return result;
}

export function ConversationSidebar({
  onNewChat,
}: ConversationSidebarProps): React.ReactElement {
  const conversations = useConversations();
  const activeId = useActiveConversationId();
  const setActiveConversation = useConversationStore((s) => s.setActiveConversation);
  const deleteConversation = useConversationStore((s) => s.deleteConversation);
  const clearAllConversations = useConversationStore((s) => s.clearAllConversations);

  const listRef = useRef<HTMLDivElement>(null);

  const timeGroups = useMemo(() => groupByTimePeriod(conversations), [conversations]);

  const handleSelect = useCallback(
    (id: string) => {
      setActiveConversation(id);
    },
    [setActiveConversation]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      deleteConversation(id);
    },
    [deleteConversation]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items = listRef.current?.querySelectorAll<HTMLElement>('[data-conversation-id]');
      if (!items?.length) return;

      const currentIndex = Array.from(items).findIndex(
        (el) => el === document.activeElement
      );

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[next]?.focus();
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prev]?.focus();
          break;
        }
        case 'Enter': {
          const enterItem = currentIndex >= 0 ? items[currentIndex] : undefined;
          const id = enterItem?.dataset['conversationId'];
          if (id) handleSelect(id);
          break;
        }
        case 'Delete': {
          const deleteItem = currentIndex >= 0 ? items[currentIndex] : undefined;
          const delId = deleteItem?.dataset['conversationId'];
          if (delId) {
            deleteConversation(delId);
            const nextFocus = items[currentIndex + 1] ?? items[currentIndex - 1];
            nextFocus?.focus();
          }
          break;
        }
      }
    },
    [handleSelect, deleteConversation]
  );

  return (
    <div className="flex h-full flex-col" data-testid="conversation-sidebar">
      {/* Sidebar header: label + new chat */}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-sm font-semibold text-muted-foreground">Conversations</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={onNewChat}
          aria-label="New chat"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          New Chat
        </Button>
      </div>

      {/* Conversation list */}
      <ScrollArea className="flex-1">
        {conversations.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-muted-foreground">
            No conversations yet
          </p>
        ) : (
          <div
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-label="Conversation history"
            onKeyDown={handleKeyDown}
            className="px-2 pb-2"
          >
            {timeGroups.map((group) => (
              <div key={group.label}>
                {/* Time period section header */}
                <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                {group.conversations.map((conv) => (
                  <div
                    key={conv.id}
                    role="option"
                    data-conversation-id={conv.id}
                    tabIndex={0}
                    aria-selected={conv.id === activeId}
                    onClick={() => handleSelect(conv.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(conv.id);
                      }
                    }}
                    className={`group flex min-h-touch min-w-0 cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm transition-colors
                      ${
                        conv.id === activeId
                          ? 'bg-accent text-accent-foreground border-l-2 border-primary'
                          : 'hover:bg-accent/50'
                      }`}
                  >
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="truncate font-medium">{conv.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {formatDistanceToNow(new Date(conv.updatedAt), { addSuffix: true })}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      tabIndex={-1}
                      className="ml-1 h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                      onClick={(e) => handleDelete(e, conv.id)}
                      aria-label={`Delete conversation: ${conv.title}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Clear All */}
      {conversations.length > 0 && (
        <div className="border-t p-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full min-h-touch text-muted-foreground hover:text-destructive"
            onClick={clearAllConversations}
            aria-label="Clear all conversations"
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
}
